import React from "react";
import { MapPin, Mail, Phone, ArrowRight, LoaderCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { cn } from "@/lib/utils";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="26492" className="flex flex-col items-center justify-center h-full min-h-[400px] space-y-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="rounded-sm bg-primary/10 p-6 ring-1 ring-primary/20">
          <Mail className="size-12 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-primary">Inquiry Received</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/20 hover:bg-primary/10 hover:text-primary rounded-sm">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Thompson" {...field} className="bg-background/50 border-input focus-visible:border-primary focus-visible:ring-primary/10 transition-all duration-300 rounded-sm text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="a.thompson@organization.com" {...field} className="bg-background/50 border-input focus-visible:border-primary focus-visible:ring-primary/10 transition-all duration-300 rounded-sm text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} className="bg-background/50 border-input focus-visible:border-primary focus-visible:ring-primary/10 transition-all duration-300 rounded-sm text-base" />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70">Include country code for international routing.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Organization / Entity</FormLabel>
              <FormControl>
                <Input placeholder="Government Agency / Developer" {...field} className="bg-background/50 border-input focus-visible:border-primary focus-visible:ring-primary/10 transition-all duration-300 rounded-sm text-base" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="budget" rules={{
      required: "Please select a target region"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Project Region</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-background/50 border-input focus:border-primary focus:ring-primary/10 transition-all duration-300 rounded-sm text-base">
                  <SelectValue placeholder="Select target region" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-card border-border text-foreground rounded-sm">
                {["Asia-Pacific", "Middle East & Africa", "Europe & Central Asia", "Americas"].map((option, i) => <SelectItem key={i} value={option} data-index={i} className="focus:bg-primary/10 focus:text-primary cursor-pointer">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormDescription className="text-xs text-muted-foreground/70">Helps us assign the correct regional consulting team.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_details" rules={{
      required: "Please provide project details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Project Brief & Objectives</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300" placeholder="Outline your vision, development goals, and required EPCO services..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-bold tracking-wider bg-primary hover:bg-primary/90 transition-colors text-primary-foreground shadow-md rounded-sm uppercase">
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 size-5 animate-spin" />
            Processing Inquiry...
          </> : <>
            Submit Global Inquiry <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
};
export default function LetsBuildTogether() {
  return <section id="global-inquiries" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-primary">
                Global Inquiries
              </h2>
              <p className="text-lg text-foreground/70 max-w-lg leading-relaxed">
                AOTO provides integrated EPCO solutions for visionary tourism developments. Connect with our experts to discuss your project requirements and development strategy.
              </p>
            </div>

            <div className="relative group rounded-sm p-px bg-border hover:bg-primary/20 transition-colors duration-500">
              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-sm blur-xl" />
              <div className="relative bg-card/90 backdrop-blur-md border border-border rounded-sm p-6 md:p-8 shadow-sm">
                <FormProvider formId="aoto_global_inquiry_connect" sectionName="global-inquiries" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                budget: "",
                project_details: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                budget: {
                  required: true
                },
                project_details: {
                  required: true
                }
              }} submitText="Submit Global Inquiry" successMessage="Your inquiry has been received. An AOTO development consultant will contact you shortly to discuss your project.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>

          {}
          <div className="relative h-full min-h-[600px] lg:min-h-auto flex flex-col">
            {}
            <div className="absolute inset-0 rounded-sm overflow-hidden border border-border shadow-md bg-muted">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.819917006856!2d103.846660314754!3d1.28333339906401!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da19085926dd21%3A0x9aa470484d859948!2sSingapore%20City%20Hall!5e0!3m2!1sen!2ssg!4v1629783587739!5m2!1sen!2ssg" width="100%" height="100%" style={{
              border: 0,
              filter: "grayscale(100%) contrast(90%) brightness(95%)"
            }} allowFullScreen loading="lazy" title="AOTO Global Headquarters" className="opacity-80 hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent pointer-events-none" />
            </div>

            {}
            <div className="relative z-10 mt-auto p-6 space-y-4">
              <Card className="bg-card/95 backdrop-blur-sm border-border shadow-sm hover:border-primary/30 transition-all duration-300 rounded-sm">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex items-center justify-center size-12 rounded-sm bg-primary/5 text-primary shrink-0">
                    <MapPin className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Global HQ</p>
                    <p className="text-base font-semibold text-foreground">RM05,9/F, THE RAINBOW, NO.22, WANG YIP ST SOUTH, YUEN LONG HONG KONG</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/95 backdrop-blur-sm border-border shadow-sm hover:border-primary/30 transition-all duration-300 rounded-sm">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex items-center justify-center size-12 rounded-sm bg-secondary/10 text-secondary shrink-0">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Consulting</p>
                    <Link to="mailto:contact@aoto-global.com" className="text-base font-semibold text-foreground hover:text-primary transition-colors">aotonexus26@gmail.com</Link>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/95 backdrop-blur-sm border-border shadow-sm hover:border-primary/30 transition-all duration-300 rounded-sm">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex items-center justify-center size-12 rounded-sm bg-accent/50 text-foreground shrink-0 border border-border">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Direct Line</p>
                    <Link to="tel:+6567890123" className="text-base font-semibold text-foreground hover:text-primary transition-colors">+00852 7019 1618</Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}